// Browser-only helpers for the uploaded creative.

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("The image couldn't be read."));
    img.src = src;
  });
}

export async function imageDimensions(src: string): Promise<{ width: number; height: number }> {
  const img = await loadImage(src);
  return { width: img.naturalWidth, height: img.naturalHeight };
}

// Claude reads images at up to ~1568 px on the long side, so anything larger only costs upload time.
const REVIEW_MAX_SIDE = 1568;

/** A JPEG copy of the image, at most REVIEW_MAX_SIDE on its long side, for the creative check. */
export async function imageForReview(src: string): Promise<Blob> {
  const img = await loadImage(src);
  const scale = Math.min(1, REVIEW_MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.naturalWidth * scale);
  canvas.height = Math.round(img.naturalHeight * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error("The image couldn't be prepared for review.");
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve, reject) =>
    canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error("The image couldn't be prepared for review."))), 'image/jpeg', 0.85),
  );
}

const FRAME_AT_SECONDS = 1;

/** A JPEG frame from about one second into the video (or its start, if shorter), with the video's size. */
export function videoFrame(src: string): Promise<{ dataUrl: string; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.onerror = () => reject(new Error("The video couldn't be read. Try an MP4 (H.264)."));
    video.onloadedmetadata = () => {
      video.currentTime = Math.min(FRAME_AT_SECONDS, (video.duration || 0) / 2);
    };
    video.onseeked = () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx || canvas.width === 0) return reject(new Error("The video couldn't be read. Try an MP4 (H.264)."));
      ctx.drawImage(video, 0, 0);
      resolve({ dataUrl: canvas.toDataURL('image/jpeg', 0.9), width: video.videoWidth, height: video.videoHeight });
    };
    video.src = src;
  });
}

export function dataUrlToFile(dataUrl: string, name: string): File {
  const [header = '', base64 = ''] = dataUrl.split(',');
  const type = /data:([^;]+)/.exec(header)?.[1] ?? 'image/jpeg';
  const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
  return new File([bytes], name, { type });
}

/** Any image a browser can draw, as a full-size JPEG File, with its preview data URL and size. */
export async function jpegFile(src: string, name: string): Promise<{ file: File; dataUrl: string; width: number; height: number }> {
  const img = await loadImage(src);
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error("The image couldn't be prepared.");
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);
  const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
  return { file: dataUrlToFile(dataUrl, name), dataUrl, width: img.naturalWidth, height: img.naturalHeight };
}
